import React from 'react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { StyleSheet, View, ViewProps } from 'react-native'
import { Button, Text } from 'zens-storybook'
import { AccessType, useStore } from 'zens-store'
import { useEffect, useMemo, useState } from 'react'

import { usePermissions } from '@dms/hook'

import { InfoBlock, InfoTable } from '..'

const styles = StyleSheet.create({
  container: {},
  info: {
    maxWidth: '100%',
  },
  skeletonRow: {
    height: 32,
  },
  skeletonTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
})

interface Props extends ViewProps {
  companyId: number
}

interface CompanyWebhookResponse {
  id: number
  url: string
}

const CompanyWebhookSettings = (props: Props) => {
  const { companyId, style, ...otherProps } = props
  const { t } = useTranslation()
  const { hasPermission } = usePermissions(companyId)
  const { api } = useStore()
  const [webhooks, setWebhooks] = useState<CompanyWebhookResponse[] | undefined>(undefined)
  const [webhooksError, setWebhooksError] = useState<string | undefined>(undefined)
  const canViewWebhooks = hasPermission(AccessType.Read, 'settings')
  const canEditWebhooks = hasPermission(AccessType.Update, 'settings')
  const isLoading = webhooks === undefined && !webhooksError
  const configuredWebhookUrls = useMemo(
    () =>
      (webhooks || [])
        .map((webhook) => webhook.url?.trim())
        .filter((url): url is string => Boolean(url && url.length > 0)),
    [webhooks],
  )
  const hasWebhooksConfigured = configuredWebhookUrls.length > 0

  useEffect(() => {
    let active = true

    if (!canViewWebhooks) {
      setWebhooks(undefined)
      setWebhooksError(undefined)
      return () => {
        active = false
      }
    }

    setWebhooks(undefined)
    setWebhooksError(undefined)

    void (async () => {
      try {
        const response = await api.get<CompanyWebhookResponse[]>(`company/${companyId}/webhooks`)

        if (!active) return

        setWebhooks(Array.isArray(response) ? response : [])
      } catch (error) {
        if (!active) return
        setWebhooks([])
        setWebhooksError(t('Webhook settings could not be loaded'))
      }
    })()

    return () => {
      active = false
    }
  }, [api, canViewWebhooks, companyId, t])

  if (!canViewWebhooks) return null

  const fields = (() => {
    const label = t('Configured webhooks')

    if (webhooksError || isLoading || !hasWebhooksConfigured) {
      return [
        {
          label,
          value: webhooksError
            ? webhooksError
            : isLoading
              ? t('Loading...')
              : t('No webhooks configured for this company'),
        },
      ]
    }

    const [firstUrl, ...otherUrls] = configuredWebhookUrls

    return [
      { label, value: firstUrl },
      ...otherUrls.map((url) => ({ label: '', value: url })),
    ]
  })()

  const config = {
    fields,
  }

  return (
    <InfoTable
      title={t('Webhooks')}
      buttonTitle={t('Edit')}
      companyId={companyId}
      contentContainerStyle={styles.info}
      buttonHref={hasWebhooksConfigured && canEditWebhooks ? `/companies/${companyId}/webhook` : undefined}
      style={[styles.container, style]}
      config={config}
      {...otherProps}
    />
  )
}

interface SkeletonProps extends ViewProps {}

const Skeleton = (props: SkeletonProps) => {
  const { t } = useTranslation()

  return (
    <InfoBlock.Skeleton
      title={
        <View style={styles.skeletonTitle}>
          <Text level={4}>{t('Webhooks')}</Text>
          <Button.Skeleton />
        </View>
      }
      {...props}
    >
      <Text.Skeleton style={styles.skeletonRow} />
    </InfoBlock.Skeleton>
  )
}

CompanyWebhookSettings.Skeleton = Skeleton

export default observer(CompanyWebhookSettings)
